import Settings from './settings';
import {
  addLayerToMap,
  findLayerById,
  removeLayerFromMap,
  hasMapInstance
} from 'utilities/map-instance.util';

const scale = window.devicePixelRatio || 1;
const nameOffset = scale > 2 ? '@3x' : scale > 1 ? '@2x' : '';

const pinMarkerSymbol = {
  type: 'picture-marker',
  url: `/ui-custom-map-point-selected${nameOffset}.png`,
  width: '32px',
  height: '32px'
};

const pinHoverMarkerSymbol = {
  type: 'picture-marker',
  url: `/ui-custom-map-point-clear-selected${nameOffset}.png`,
  width: '32px',
  height: '32px'
};

/**
 * Remove the hover tooltip for the pin (if any) and reset cursor.
 * This centralises tooltip cleanup so it can be called from both hover
 * handlers and any place that programmatically removes the pin layer.
 */
const clearPinTooltip = () => {
  const { loadedView } = window;

  if (!loadedView) {
    return;
  }

  // Reset cursor back to default
  if (loadedView.container && loadedView.container.style) {
    loadedView.container.style.cursor = '';
  }

  // Remove tooltip DOM node if it exists
  const existingTooltip = document.getElementById('mls-pin-tooltips');
  if (existingTooltip && existingTooltip.parentNode) {
    existingTooltip.parentNode.removeChild(existingTooltip);
  }

  // Best-effort removal from ArcGIS UI in case it was registered there
  try {
    loadedView.ui && loadedView.ui.remove('mls-pin-tooltips');
  } catch {
    // Fail silently – DOM removal above is the primary cleanup path
  }
};

export const removePin = () => {
  // Use centralized utility to remove layer
  clearPinTooltip();
  if (hasMapInstance()) {
    removeLayerFromMap('mis-pin-layer');
  }
};

export const removePinAndBuffer = () => {
  // Use centralized utility to remove layers
  clearPinTooltip();
  if (hasMapInstance()) {
    removeLayerFromMap('mis-pin-layer');
    removeLayerFromMap('mis-buffer-layer');
  }
};

export const addPin = (center: any[], popupTemplate?: any) => {
  const { loadedMap, loadedMapModules, loadedView } = window;
  if (loadedMap && loadedMapModules) {
    const { Graphic, GraphicsLayer } = loadedMapModules;
    const pinLayer = new GraphicsLayer({ id: 'mis-pin-layer' });
    const point = {
      type: 'point',
      longitude: center[0],
      latitude: center[1]
    };
    const pin = new Graphic({
      geometry: point,
      symbol: pinMarkerSymbol
    });

    if (popupTemplate && loadedView) {
      loadedView.openPopup({
        content: popupTemplate.content(),
        location: pin.geometry
      });
      if (loadedView.popup) {
        loadedView.popup.visibleElements = {
          featureNavigation: false,
          closeButton: false
        };
        loadedView.popup.actions = null;
        loadedView.popup.alignment = 'bottom-center';
        loadedView.popup.dockOptions = {
          buttonEnabled: false,
          position: 'bottom-center'
        };
        if (loadedView.popup.viewModel) {
          loadedView.popup.viewModel.includeDefaultActions = false;
        }
      }
    }
    pinLayer.add(pin);
    // Use centralized utility to add layer
    addLayerToMap(pinLayer);
  }
};

export const addPinAndBuffer = (center: number[]) => {
  const { loadedView, loadedMap, loadedMapModules } = window;
  if (loadedMap && loadedMapModules) {
    const { Graphic, GraphicsLayer, GeometryEngine, Point } = loadedMapModules;
    const pinLayer = new GraphicsLayer({ id: 'mis-pin-layer' });
    const bufferLayer = new GraphicsLayer({ id: 'mis-buffer-layer' });
    const point = {
      type: 'point',
      longitude: center[0],
      latitude: center[1]
    };
    const pin = new Graphic({
      geometry: point,
      symbol: pinMarkerSymbol
    });

    const newPoint = new Point(point);

    const buffer = GeometryEngine.geodesicBuffer(newPoint, 200, 'meters');
    const newGeom = new Graphic({
      geometry: buffer,
      symbol: Settings.polygonSymbolNoOutline
    });

    bufferLayer.add(newGeom);
    // Use centralized utility to add layers
    addLayerToMap(bufferLayer);

    pinLayer.add(pin);
    addLayerToMap(pinLayer);

    if (loadedView && !loadedView.destroyed) {
      loadedView.goTo({ target: newGeom, zoom: 15 }).catch((error: any) => {
        if (error.name !== 'AbortError') {
          console.warn('Error zooming to pin:', error);
        }
      });
    }
  }
};

export const hoverPin = (
  over: boolean,
  screenPoint: { x: number; y: number }
) => {
  const { loadedView } = window;
  // Use centralized utility to find layer
  var pinLayer: any = findLayerById('mis-pin-layer');
  if (pinLayer) {
    pinLayer.graphics.forEach(function (g: any) {
      g.symbol = over ? pinHoverMarkerSymbol : pinMarkerSymbol;
    });

    if (over && loadedView) {
      const existingTooltip = document.getElementById('mls-pin-tooltips');
      loadedView.container.style.cursor = 'pointer';
      if (!existingTooltip) {
        const tooltip = document.createElement('div');
        const tootipTextNode = document.createTextNode('Your location');
        tooltip.className = 'mls-pin-tooltips';
        tooltip.id = 'mls-pin-tooltips';
        tooltip.style.left = `${screenPoint.x - 100}px`;
        tooltip.style.top = `${screenPoint.y + 20}px`;
        tooltip.appendChild(tootipTextNode);
        loadedView.ui.add(tooltip, 'manual');
      } else {
        existingTooltip.style.left = `${screenPoint.x - 45}px`;
        existingTooltip.style.top = `${screenPoint.y + 20}px`;
      }
    } else {
      clearPinTooltip();
    }
  }
};

export const zoomToCenter = (
  center: number[],
  isECContacts = false,
  cb?: any
) => {
  const { loadedView } = window;
  if (loadedView && !loadedView.destroyed) {
    if (isECContacts) {
      removePinAndBuffer();
      addPinAndBuffer(center);
      cb(center);
    } else {
      removePin();
      addPin(center);
      loadedView
        .goTo({ center, zoom: Settings.zoomLevel })
        .catch((error: any) => {
          if (error.name !== 'AbortError') {
            console.warn('Error zooming to center:', error);
          }
        });
    }
  }
};

export const zoomToFeature = (
  extent: any,
  extentRadius: number = 1,
  expand?: boolean,
  unit: string = 'kilometers'
) => {
  const {
    loadedView,
    loadedMapModules
  }: { loadedView: __esri.MapView; loadedMapModules: any } = window;

  if (loadedMapModules && loadedView && !loadedView.destroyed) {
    const { GeometryEngine } = loadedMapModules;
    const bufferExtent = GeometryEngine.geodesicBuffer(
      extent,
      extentRadius,
      unit
    );

    let bufferExtentVal = bufferExtent.extent;

    if (expand) {
      const newExtent = bufferExtent.extent.clone().expand(1.3);
      bufferExtentVal = newExtent;
    }

    // Ensure view is ready before calling goTo, especially after basemap changes
    // This prevents "Cannot read properties of undefined (reading 'animation')" errors
    const performZoom = () => {
      if (loadedView && !loadedView.destroyed) {
        return loadedView
          .goTo(bufferExtentVal)
          .then(() => {
            // Save the extent after zooming so it persists across page navigation
            if (loadedView && !loadedView.destroyed) {
              window.mapOriginalExtent = loadedView.extent;
            }
          })
          .catch((error: any) => {
            if (error.name !== 'AbortError') {
              console.warn('Error zooming to feature:', error);
            }
            // If goTo fails, still try to save the extent
            if (loadedView && !loadedView.destroyed) {
              window.mapOriginalExtent = bufferExtentVal;
            }
          });
      }
    };

    // Always wait for view to be ready before zooming
    // This is especially important after basemap changes when view might be reinitializing
    console.log('[zoomToFeature] Ensuring view is ready before zooming');
    loadedView
      .when(() => {
        console.log('[zoomToFeature] View ready, performing zoom');
        performZoom();
      })
      .catch((error: any) => {
        console.warn(
          '[zoomToFeature] Error waiting for view to be ready:',
          error
        );
        // Try anyway if waiting fails (view might already be ready)
        performZoom();
      });
  } else {
    console.warn('[zoomToFeature] View or modules not available:', {
      hasView: !!loadedView,
      viewDestroyed: loadedView?.destroyed,
      hasModules: !!loadedMapModules
    });
  }
};

export const calculateArea = (
  geometry: any,
  unit: string = 'square-kilometers'
) => {
  const { loadedMapModules } = window;
  if (loadedMapModules) {
    const { GeometryEngine } = loadedMapModules;
    return GeometryEngine.geodesicArea(geometry, unit);
  }
  return '';
};

const CLONED_SHAPE_LAYER_IDS = ['cloned-shape-layer', 'drawing-layer'];

export const restoreViewToClonedShape = (shapeArea?: number) => {
  const {
    loadedMap,
    loadedView,
    loadedMapModules
  }: {
    loadedMap: any;
    loadedView: __esri.MapView;
    loadedMapModules: any;
  } = window;

  if (!loadedMap || !loadedView || loadedView.destroyed || !loadedMapModules) {
    return;
  }

  for (const layerId of CLONED_SHAPE_LAYER_IDS) {
    const layer = loadedMap.findLayerById(layerId);
    const graphic = layer?.graphics?.getItemAt?.(0);
    const extent = graphic?.geometry?.extent;
    if (!extent) {
      continue;
    }

    const area =
      shapeArea ?? calculateArea(graphic.geometry, 'square-kilometers');
    const unitToUse = area <= 0.01 ? 'meters' : 'kilometers';

    const performZoom = () => {
      zoomToFeature(extent, 1, false, unitToUse);
    };

    loadedView
      .when(() => {
        if (layer) {
          loadedView.whenLayerView(layer).then(performZoom).catch(performZoom);
        } else {
          performZoom();
        }
      })
      .catch(performZoom);

    return;
  }
};
